import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gauge, ArrowRight, Clock } from 'lucide-react';
import api from '../lib/api';

const NEXT = { free: 'pro', pro: 'premium', premium: 'enterprise' };
const CAPS = { free: 5, pro: 50, premium: 200, enterprise: 'Unlimited' };
const LABEL = { free: 'Free', pro: 'Standard', premium: 'Premium', enterprise: 'Enterprise' };

export const isQuotaError = (e) => e?.response?.status === 429 && e?.response?.data?.detail?.code === 'DAILY_QUERY_LIMIT';

export function QuotaLimitCard({ detail, context = 'this page' }) {
  const [plans, setPlans] = useState(null);
  useEffect(() => { api.get('/billing/catalogue').then(r => setPlans(r.data?.plans || null)).catch(() => {}); }, []);
  const plan = detail?.plan || 'free';
  const nextId = NEXT[plan];
  const next = nextId && plans?.[nextId];
  const price = next?.price_monthly ? `£${(next.price_monthly / 100).toLocaleString()}/mo` : null;
  const resetsAt = detail?.resets_at ? new Date(detail.resets_at) : null;
  const hrs = resetsAt ? Math.max(1, Math.round((resetsAt - Date.now()) / 3600000)) : null;

  return (
    <div className="solid-card overflow-hidden animate-fade-in" data-testid="quota-limit-card">
      <div className="p-6 sm:p-8 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center">
        <div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-light text-brand-orange font-body text-[11px] font-semibold uppercase tracking-wider"><Gauge className="w-3.5 h-3.5" /> Daily limit reached</div>
          <h3 className="font-serif text-2xl sm:text-3xl font-bold text-ink mt-3 leading-tight">You've used today's {detail?.cap ?? CAPS[plan]} queries on the {LABEL[plan]} plan.</h3>
          <p className="font-body text-sm text-ink-2 mt-2 leading-relaxed">
            {context} will pick up again {hrs ? `in about ${hrs} hour${hrs > 1 ? 's' : ''}` : 'at midnight UTC'}.
            {nextId && <> Upgrade to <b className="text-ink">{LABEL[nextId]}</b> for <b className="text-ink">{CAPS[nextId] === 'Unlimited' ? 'unlimited' : `${CAPS[nextId]} queries a day`}</b>{price && <> from <b className="text-ink">{price}</b></>}{next?.trial_days ? `, ${next.trial_days}-day free trial` : ''}.</>}
          </p>
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            {nextId && <Link to="/pricing" data-testid="quota-upgrade-btn" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-brand-orange text-white font-body text-sm font-semibold hover:bg-highlight-orange transition-colors">Upgrade to {LABEL[nextId]} <ArrowRight className="w-4 h-4" /></Link>}
            <span className="inline-flex items-center gap-1.5 font-body text-xs text-mid" data-testid="quota-reset-hint"><Clock className="w-3.5 h-3.5" /> Resets {resetsAt ? resetsAt.toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' }) : 'daily at 00:00 UTC'}</span>
          </div>
        </div>
        {nextId && (
          <div className="rounded-[20px] bg-bg-warm border border-warm-border p-5 min-w-[200px] text-center" data-testid="quota-next-tier">
            <p className="font-body text-[11px] uppercase tracking-wider text-mid font-semibold">Next tier</p>
            <p className="font-display text-xl font-bold text-ink mt-1">{LABEL[nextId]}</p>
            <p className="font-display text-3xl font-extrabold text-brand-orange mt-1" data-testid="quota-next-price">{price || (CAPS[nextId] === 'Unlimited' ? 'Talk to us' : '—')}</p>
            <p className="font-body text-xs text-ink-2 mt-1">{CAPS[nextId] === 'Unlimited' ? 'Unlimited queries' : `${CAPS[nextId]} queries / day`}</p>
            {next?.tagline && <p className="font-body text-[11px] text-mid mt-2">{next.tagline}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
